import PendingTaskShortcuts from 'components/organisms/pending-task-shortcuts'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { useAuth } from 'providers/auth-provider'
import { Button, Card, CardBody, Form, Input, InputGroup, InputGroupAddon } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import Phase3ProductQueueTable from '../components/Phase3ProductQueueTable'
import Phase3QueuePagination from '../components/Phase3QueuePagination'
import {
  PHASE3_PRODUCT_SCOPES,
  PHASE3_SCOPE_LABELS,
  PHASE3_JULY20_APPRAISAL_CATEGORIES,
  PHASE3_JULY20_APPRAISAL_CATEGORY_LABELS,
  phase3July20FixedCategoryForUser,
  phase3July20ShowCategoryDropdown
} from '../constants'
import { extractPhase3Meta, extractPhase3Products, getPhase3ErrorMessage } from '../helpers/phase3-inbound-journey-helper'

const propTypes = {
  scope        : PropTypes.string,
  title        : PropTypes.string,
  apiNamespace        : PropTypes.string,
  embedded            : PropTypes.bool,
  inventoryViewControl: PropTypes.node,
  pageUtils           : PropTypes.object
}

const defaultProps = {
  scope        : '',
  title        : '',
  apiNamespace         : '',
  embedded             : false,
  inventoryViewControl : null,
  pageUtils            : {}
}

const PAGINATED_QUEUE_SCOPES = [
  PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED,
  PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_DONE,
  PHASE3_PRODUCT_SCOPES.STUDIO,
  PHASE3_PRODUCT_SCOPES.PRICE_TAG,
  PHASE3_PRODUCT_SCOPES.READY_TO_LIVE,
  PHASE3_PRODUCT_SCOPES.READY_FOR_INBOUND,
  PHASE3_PRODUCT_SCOPES.WAITING_FOR_INBOUND,
  PHASE3_PRODUCT_SCOPES.INBOUND_COMPLETED,
  PHASE3_PRODUCT_SCOPES.LIVE,
  PHASE3_PRODUCT_SCOPES.DELETED,
  PHASE3_PRODUCT_SCOPES.UNPROCESSED,
  PHASE3_PRODUCT_SCOPES.INCOMING,
  PHASE3_PRODUCT_SCOPES.BKT
]

const APPRAISAL_CATEGORY_SCOPES = [
  PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED,
  PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_DONE
]

const OPERATIONAL_CATEGORY_SCOPES = [
  PHASE3_PRODUCT_SCOPES.STUDIO,
  PHASE3_PRODUCT_SCOPES.LIVE,
  PHASE3_PRODUCT_SCOPES.PRICE_TAG,
  PHASE3_PRODUCT_SCOPES.DELETED,
  PHASE3_PRODUCT_SCOPES.UNPROCESSED,
  PHASE3_PRODUCT_SCOPES.INCOMING,
  PHASE3_PRODUCT_SCOPES.BKT
]

const INVENTORY_EDIT_SKU_SCOPES = [
  PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED,
  PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_DONE,
  PHASE3_PRODUCT_SCOPES.PRICE_TAG,
  PHASE3_PRODUCT_SCOPES.READY_TO_LIVE,
  PHASE3_PRODUCT_SCOPES.READY_FOR_INBOUND,
  PHASE3_PRODUCT_SCOPES.WAITING_FOR_INBOUND,
  PHASE3_PRODUCT_SCOPES.INBOUND_COMPLETED,
  PHASE3_PRODUCT_SCOPES.LIVE,
  PHASE3_PRODUCT_SCOPES.DELETED,
  PHASE3_PRODUCT_SCOPES.UNPROCESSED,
  PHASE3_PRODUCT_SCOPES.INCOMING,
  PHASE3_PRODUCT_SCOPES.BKT
]

const PRODUCT_CURATOR_FILTER_SCOPES = [
  PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED,
  PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE,
  PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL
]
const PAGINATED_QUEUE_DEFAULT_PER_PAGE = 25
const LEGACY_QUEUE_DEFAULT_PER_PAGE = 20
const PAGINATED_QUEUE_PER_PAGE_OPTIONS = [ 25, 50, 100 ]

const SKIP_CONFIRM_BUYING_PRICE_GROUP_CODES = [ 'BKA', 'BKO', 'BKV' ]

const productShouldSkipConfirmBuyingPrice = (product = {}) => {
  if (typeof product.skip_confirm_buying_price === 'boolean') return product.skip_confirm_buying_price

  return !!product.skip_confirm_buying_price || SKIP_CONFIRM_BUYING_PRICE_GROUP_CODES.includes((product.group_code || '').toString().trim().toUpperCase())
}

const positiveInteger = (value, fallback) => {
  const parsedValue = parseInt(value, 10)
  return Number.isNaN(parsedValue) || parsedValue < 1 ? fallback : parsedValue
}

const queuePerPage = (value, fallback, useEnhancedPagination) => {
  const parsedValue = positiveInteger(value, fallback)
  if (!useEnhancedPagination) return parsedValue

  return PAGINATED_QUEUE_PER_PAGE_OPTIONS.includes(parsedValue) ? parsedValue : fallback
}

const Phase3ProductQueuePage = ({ scope, title, pageUtils, apiNamespace = '', embedded = false, inventoryViewControl = null }) => {
  const history = useHistory()
  const location = useLocation()
  const { user } = useAuth()
  const params = new URLSearchParams(location.search)

  const [ q, setQ ] = useState(params.get('q') || '')
  const [ products, setProducts ] = useState([])
  const [ meta, setMeta ] = useState({})
  const [ isLoading, setIsLoading ] = useState(false)
  const [ submittingProductId, setSubmittingProductId ] = useState(null)
  const [ isBulkApproving, setIsBulkApproving ] = useState(false)
  const [ hasUnsavedAppraisalChanges, setHasUnsavedAppraisalChanges ] = useState(false)

  const useEnhancedPagination = PAGINATED_QUEUE_SCOPES.includes(scope)
  const defaultPerPage = useEnhancedPagination ? PAGINATED_QUEUE_DEFAULT_PER_PAGE : LEGACY_QUEUE_DEFAULT_PER_PAGE
  const page = positiveInteger(params.get('page'), 1)
  const perPage = queuePerPage(params.get('per_page'), defaultPerPage, useEnhancedPagination)
  const currentQuery = params.get('q') || ''
  const useAppraisalCategoryFilter = APPRAISAL_CATEGORY_SCOPES.includes(scope)
  const fixedAppraisalCategory = phase3July20FixedCategoryForUser(user)
  const showAppraisalCategoryDropdown = useAppraisalCategoryFilter && phase3July20ShowCategoryDropdown(user)
  const requestedAppraisalCategory = params.get('appraisal_category') || ''
  const selectedAppraisalCategory = PHASE3_JULY20_APPRAISAL_CATEGORIES.includes(requestedAppraisalCategory)
    ? requestedAppraisalCategory
    : ''
  const effectiveAppraisalCategory = fixedAppraisalCategory || (showAppraisalCategoryDropdown ? selectedAppraisalCategory : '')
  const useOperationalCategoryFilter = OPERATIONAL_CATEGORY_SCOPES.includes(scope)
  const requestedInternalCategory = params.get('internal_category') || ''
  const operationalCategoryOptions = scope === PHASE3_PRODUCT_SCOPES.STUDIO
    ? [ ...PHASE3_JULY20_APPRAISAL_CATEGORIES, 'STC' ]
    : PHASE3_JULY20_APPRAISAL_CATEGORIES
  const selectedInternalCategory = operationalCategoryOptions.includes(requestedInternalCategory) ? requestedInternalCategory : ''
  const internalCategoryCounts = meta.internal_category_counts || {}
  const useProductCuratorFilter = PRODUCT_CURATOR_FILTER_SCOPES.includes(scope) &&
    !(scope === PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED && [ 'finance', 'buying' ].includes(apiNamespace))
  const selectedProductCuratorPic = useProductCuratorFilter ? (params.get('product_curator_pic') || '') : ''
  const productCuratorOptions = Array.isArray(meta.product_curator_pics) ? meta.product_curator_pics : []
  const selectedProductCuratorOptionExists = productCuratorOptions.some((option) => option === selectedProductCuratorPic)

  const buildSearch = ({
    q: nextQuery = '',
    page: nextPage = 1,
    perPage: nextPerPage = perPage,
    appraisalCategory: nextAppraisalCategory = effectiveAppraisalCategory,
    internalCategory: nextInternalCategory = selectedInternalCategory,
    productCuratorPic: nextProductCuratorPic = selectedProductCuratorPic
  }) => {
    const search = new URLSearchParams(location.search)
    const normalizedQuery = nextQuery.toString().trim()

    search.delete('q')
    search.delete('page')
    search.delete('per_page')
    search.delete('appraisal_category')
    search.delete('internal_category')
    search.delete('product_curator_pic')

    if (normalizedQuery) search.set('q', normalizedQuery)
    search.set('page', positiveInteger(nextPage, 1).toString())
    if (useEnhancedPagination) search.set('per_page', nextPerPage.toString())
    if (useAppraisalCategoryFilter && nextAppraisalCategory) {
      search.set('appraisal_category', nextAppraisalCategory)
    }
    if (useOperationalCategoryFilter && nextInternalCategory) {
      search.set('internal_category', nextInternalCategory)
    }
    if (useProductCuratorFilter && nextProductCuratorPic) {
      search.set('product_curator_pic', nextProductCuratorPic)
    }

    return search.toString()
  }

  const pushSearch = (nextParams, { replace = false } = {}) => {
    const nextLocation = {
      pathname: location.pathname,
      search: buildSearch(nextParams)
    }

    if (replace) {
      history.replace(nextLocation)
      return
    }

    history.push(nextLocation)
  }

  const confirmQueueNavigation = () => {
    const editablePriceScope = [ PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED, PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL ].includes(scope)
    if (!editablePriceScope || !hasUnsavedAppraisalChanges) return true

    return window.confirm('Perubahan harga yang belum di-Submit akan hilang. Tetap lanjut?')
  }

  useEffect(() => {
    setQ(new URLSearchParams(location.search).get('q') || '')
  }, [ location.search ])

  useEffect(() => {
    fetchProducts()
  }, [ location.search, scope, apiNamespace ]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
    setIsLoading(true)
    try {
      const response = await InboundJourneyApiV2.getProducts({
        scope: scope,
        q: currentQuery,
        page: page,
        per_page: perPage,
        appraisalCategory: useAppraisalCategoryFilter ? effectiveAppraisalCategory : '',
        internalCategory: useOperationalCategoryFilter ? selectedInternalCategory : '',
        productCuratorPic: selectedProductCuratorPic,
        pendingTask: params.get('pending_task') || '',
        apiNamespace: apiNamespace
      })
      const nextProducts = extractPhase3Products(response)
      const nextMeta = extractPhase3Meta(response)
      const totalCount = Math.max(0, Number(nextMeta.total_count) || 0)
      const totalPages = Math.max(1, Number(nextMeta.total_pages) || 1)

      if (useEnhancedPagination && page > totalPages) {
        pushSearch({
          q: currentQuery,
          page: totalCount > 0 ? totalPages : 1,
          perPage: perPage
        }, { replace: true })
        return
      }

      setProducts(nextProducts)
      setMeta(nextMeta)
      if ([ PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED, PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL ].includes(scope)) setHasUnsavedAppraisalChanges(false)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitSearch = (event) => {
    event.preventDefault()
    if (!confirmQueueNavigation()) return

    pushSearch({ q: q, page: 1, perPage: perPage })
  }

  const handleResetSearch = () => {
    if (!confirmQueueNavigation()) return

    setQ('')
    pushSearch({ q: '', page: 1, perPage: perPage, productCuratorPic: '' })
  }

  const handlePage = (nextPage) => {
    if (!confirmQueueNavigation()) return

    pushSearch({ q: currentQuery, page: nextPage, perPage: perPage })
  }

  const handlePerPage = (nextPerPage) => {
    if (!confirmQueueNavigation()) return

    pushSearch({ q: currentQuery, page: 1, perPage: nextPerPage })
  }

  const handleAppraisalCategoryChange = (event) => {
    if (!confirmQueueNavigation()) return

    pushSearch({
      q: currentQuery,
      page: 1,
      perPage: perPage,
      appraisalCategory: event.target.value
    })
  }

  const handleInternalCategoryChange = (event) => {
    pushSearch({
      q: currentQuery,
      page: 1,
      perPage: perPage,
      internalCategory: event.target.value
    })
  }

  const handleProductCuratorChange = (event) => {
    if (!confirmQueueNavigation()) return

    pushSearch({
      q: currentQuery,
      page: 1,
      perPage: perPage,
      productCuratorPic: event.target.value
    })
  }


  const handleSubmitAppraisal = async (product, appraisalForm) => {
    const canSubmitSellingPrice = product.can_view_selling_price !== false &&
      product.can_edit_selling_price !== false
    const requiredFields = [
      'buying_price',
      'default_price',
      ...(canSubmitSellingPrice ? [ 'selling_price' ] : [])
    ]
    const missingFields = requiredFields.filter((field) => {
      const value = appraisalForm[field]
      return value == null || value.toString().trim() === '' || Number(value) <= 0
    })

    if (missingFields.length > 0) {
      const message = `Please fill ${missingFields.join(', ')} before submit.`
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(message) : alert(message)
      return
    }

    setSubmittingProductId(product.id)
    try {
      const appraisalPayload = {
        buying_price   : appraisalForm.buying_price,
        default_price  : appraisalForm.default_price,
        appraisal_note : appraisalForm.appraisal_note
      }
      if (canSubmitSellingPrice) appraisalPayload.selling_price = appraisalForm.selling_price

      const response = await InboundJourneyApiV2.fillAppraisal({
        id: product.id,
        product: appraisalPayload,
        skip_confirm_buying_price: productShouldSkipConfirmBuyingPrice(product),
        apiNamespace: apiNamespace
      })

      pageUtils?.setAlertMsg?.(response.data?.message || 'Appraisal saved successfully')
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setSubmittingProductId(null)
    }
  }

  const handleConfirmBuyingPrice = async (product) => {
    setSubmittingProductId(product.id)
    try {
      const response = await InboundJourneyApiV2.confirmBuyingPrice({
        id: product.id,
        product: {
          buying_price: product.buying_price
        },
        apiNamespace: apiNamespace
      })

      pageUtils?.setAlertMsg?.(response.data?.message || 'CF Buying Price confirmed successfully')
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setSubmittingProductId(null)
    }
  }

  const handleRejectBuyingPrice = async (product, appraisalNote) => {
    setSubmittingProductId(product.id)
    try {
      const response = await InboundJourneyApiV2.rejectBuyingPrice({
        id: product.id,
        appraisal_note: appraisalNote,
        apiNamespace: apiNamespace
      })

      pageUtils?.setAlertMsg?.(response.data?.message || 'CF Buying Price rejected successfully')
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setSubmittingProductId(null)
    }
  }

  const handleApproveAppraisal = async (product, appraisalForm = {}) => {
    setSubmittingProductId(product.id)
    try {
      const response = await InboundJourneyApiV2.approveAppraisal({
        id: product.id,
        product: {
          buying_price  : appraisalForm.buying_price,
          default_price : appraisalForm.default_price,
          selling_price : appraisalForm.selling_price
        },
        apiNamespace: apiNamespace
      })

      pageUtils?.setAlertMsg?.(response.data?.message || 'Appraisal approved successfully')
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setSubmittingProductId(null)
    }
  }

  const handleBulkApproveAppraisal = async (productApprovals) => {
    if (!productApprovals || productApprovals.length === 0) return

    const productIds = productApprovals.map((product) => product.id)

    setIsBulkApproving(true)
    try {
      const response = await InboundJourneyApiV2.bulkApproveAppraisal({
        product_ids: productIds,
        products: productApprovals,
        apiNamespace: apiNamespace
      })
      const approvedCount = response.data?.meta?.approved_count || productIds.length

      pageUtils?.setAlertMsg?.(response.data?.message || `${approvedCount} appraisals approved successfully`)
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsBulkApproving(false)
    }
  }

  const handleRejectAppraisal = async (product, appraisalNote) => {
    setSubmittingProductId(product.id)
    try {
      const response = await InboundJourneyApiV2.rejectAppraisal({
        id: product.id,
        appraisal_note: appraisalNote,
        apiNamespace: apiNamespace
      })

      pageUtils?.setAlertMsg?.(response.data?.message || 'Appraisal rejected successfully')
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setSubmittingProductId(null)
    }
  }

  const handleManualGoLive = async (product) => {
    const confirmed = window.confirm(`Make ${product.sku || 'this product'} live now?`)
    if (!confirmed) return

    setSubmittingProductId(product.id)
    try {
      const response = await InboundJourneyApiV2.goLive({
        id: product.id,
        location: product.item_location_name || product.item_location_label || 'Warehouse',
        apiNamespace: apiNamespace
      })

      pageUtils?.setAlertMsg?.(response.data?.message || 'Product is live')
      await fetchProducts()
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setSubmittingProductId(null)
    }
  }

const getProductUrl = (product) => {
  if (INVENTORY_EDIT_SKU_SCOPES.includes(scope)) {
    const inventoryId = product.inventory?.id

    return inventoryId ? `/app/warehouse-team/inventory/${inventoryId}` : ''
  }

  return `/app/inbound-journey/products/${product.id}?from=${encodeURIComponent(location.pathname + location.search)}&api_namespace=${encodeURIComponent(apiNamespace)}`
}

const handleOpenProduct = (product) => {
  const productUrl = getProductUrl(product)

  if (!productUrl) {
    const message = `Inventory untuk SKU ${product.sku || '-'} belum tersedia.`
    pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(message) : alert(message)
    return
  }

  history.push(productUrl)
}

  const displayTitle = title || PHASE3_SCOPE_LABELS[scope] || 'Products'
  const queueHint = (() => {
    if (scope === PHASE3_PRODUCT_SCOPES.STUDIO) {
      return 'Item Pending dihitung jika Foto Product - Front View atau Foto Kelengkapan belum diunggah. Setelah kedua foto tersedia, item keluar dari antrian ini. Link Google Drive, Stamp, dan Card tidak memengaruhi counter. Scope produk tetap mengikuti In Process di dashboard lama.'
    }

    if (scope === PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED) {
      return 'Appraisal dan Kelengkapan / Studio dapat dikerjakan paralel. Appraisal tidak perlu menunggu Studio Complete.'
    }

    if (scope === PHASE3_PRODUCT_SCOPES.PRICE_TAG) {
      return 'Final check adalah tahap terakhir sebelum live. Lengkapi price tag, deskripsi, kelengkapan, catatan kondisi, dan lokasi produk sebelum bulk-live.'
    }

    if (scope === PHASE3_PRODUCT_SCOPES.READY_TO_LIVE) {
      return 'Auto Live berjalan berkala dari backend. Gunakan tombol checklist hijau untuk Manual Live jika customer sedang menunggu, atau gunakan Inventory Batch untuk bulk-live.'
    }

    if (scope === PHASE3_PRODUCT_SCOPES.READY_FOR_INBOUND) {
      return 'Sama seperti Dashboard Lama Inbounds > Ready For Inbound: produk aktif yang masih menunggu inbound dan sudah memenuhi seluruh syarat kelengkapan inbound.'
    }

    if (scope === PHASE3_PRODUCT_SCOPES.WAITING_FOR_INBOUND) {
      return 'Sama seperti Dashboard Lama Inbounds > Waiting For Inbound: semua produk aktif yang masih menunggu proses inbound, termasuk produk yang belum lengkap.'
    }

    if (scope === PHASE3_PRODUCT_SCOPES.INBOUND_COMPLETED) {
      return 'Sama seperti Dashboard Lama Inbounds > Completed: produk yang proses inbound-nya sudah disubmit / selesai.'
    }

    return ''
  })()

  return (
    <>
      { !embedded && <TitlePage mainTitle='Inbound Journey' subTitle={ displayTitle } /> }
      { !embedded && <PendingTaskShortcuts tasks={ ['appraisal_needed', 'confirm_buying_price', 'appraisal_approval'] } /> }

      { [
        PHASE3_PRODUCT_SCOPES.STUDIO
      ].includes(scope) && (
        <Card className='mb-3'>
          <CardBody className='d-flex justify-content-between align-items-center'>
            <div>
              <div className='text-muted'>Item Pending</div>
              <div className='h2 mb-0'>{ meta.total_count ?? products.length }</div>
            </div>
            <div className='text-muted text-right'>
              { scope === PHASE3_PRODUCT_SCOPES.STUDIO
                ? 'Item yang menunggu kelengkapan Studio / tindakan foto detail'
                : scope === PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE
                  ? 'BKR items waiting for CF Buying Price action'
                  : scope === PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL
                    ? 'Items waiting for appraisal approval'
                    : 'Items waiting for appraisal action' }
            </div>
          </CardBody>
        </Card>
      ) }

      { queueHint && (
        <Card className='mb-3'>
          <CardBody>
            <div className='text-muted'>{ queueHint }</div>
          </CardBody>
        </Card>
      ) }

      <Card className='mb-3'>
        <CardBody>
          <div className='d-flex flex-column flex-lg-row align-items-lg-center'>
            { inventoryViewControl }
            { useOperationalCategoryFilter && (
              <div className='mb-2 mb-lg-0 mr-lg-3' style={{ minWidth: 190 }}>
                <Input
                  type='select'
                  value={ selectedInternalCategory }
                  onChange={ handleInternalCategoryChange }
                >
                  <option value=''>{ scope === PHASE3_PRODUCT_SCOPES.STUDIO ? 'Semua H/W/C/STC' : 'Semua H/W/C' }</option>
                  { operationalCategoryOptions.map((category) => (
                    <option key={ category } value={ category }>
                      { `${category === 'STC' ? 'STC' : (PHASE3_JULY20_APPRAISAL_CATEGORY_LABELS[category] || category)} (${internalCategoryCounts[category] ?? 0})` }
                    </option>
                  )) }
                </Input>
              </div>
            ) }
            { useProductCuratorFilter && (
              <div className='mb-2 mb-lg-0 mr-lg-3' style={{ minWidth: 190 }}>
                <Input
                  type='select'
                  value={ selectedProductCuratorPic }
                  onChange={ handleProductCuratorChange }
                >
                  <option value=''>Semua PC</option>
                  { selectedProductCuratorPic && !selectedProductCuratorOptionExists && (
                    <option value={ selectedProductCuratorPic }>{ selectedProductCuratorPic }</option>
                  ) }
                  { productCuratorOptions.map((productCuratorPic) => (
                    <option key={ productCuratorPic } value={ productCuratorPic }>
                      { productCuratorPic.charAt(0).toUpperCase() + productCuratorPic.slice(1).toLowerCase() }
                    </option>
                  )) }
                </Input>
              </div>
            ) }
            { showAppraisalCategoryDropdown && (
              <div className='mb-2 mb-lg-0 mr-lg-3' style={{ minWidth: 190 }}>
                <Input
                  type='select'
                  value={ selectedAppraisalCategory }
                  onChange={ handleAppraisalCategoryChange }
                >
                  <option value=''>Semua H/W/C</option>
                  { PHASE3_JULY20_APPRAISAL_CATEGORIES.map((category) => (
                    <option key={ category } value={ category }>
                      { PHASE3_JULY20_APPRAISAL_CATEGORY_LABELS[category] || category }
                    </option>
                  )) }
                </Input>
              </div>
            ) }
            <Form className='flex-grow-1' onSubmit={ handleSubmitSearch }>
              <InputGroup>
                <Input
                  value={ q }
                  placeholder='Cari SKU, nama produk, internal keyword, atau consignor'
                  onChange={ (event) => setQ(event.target.value) }
                />
                <InputGroupAddon addonType='append'>
                  <Button color='primary'>Cari</Button>
                </InputGroupAddon>
                <InputGroupAddon addonType='append'>
                  <Button type='button' color='secondary' onClick={ handleResetSearch }>
                    Reset
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </Form>
            { [
              PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED,
              PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE,
              PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL,
              PHASE3_PRODUCT_SCOPES.APPRAISAL_DONE,
              PHASE3_PRODUCT_SCOPES.READY_TO_LIVE,
              PHASE3_PRODUCT_SCOPES.READY_FOR_INBOUND,
              PHASE3_PRODUCT_SCOPES.WAITING_FOR_INBOUND,
              PHASE3_PRODUCT_SCOPES.INBOUND_COMPLETED,
              PHASE3_PRODUCT_SCOPES.PRICE_TAG,
              PHASE3_PRODUCT_SCOPES.LIVE,
              PHASE3_PRODUCT_SCOPES.DELETED,
              PHASE3_PRODUCT_SCOPES.UNPROCESSED,
              PHASE3_PRODUCT_SCOPES.INCOMING,
              PHASE3_PRODUCT_SCOPES.BKT
            ].includes(scope) && (
              <div
                className='d-flex align-items-center justify-content-between border rounded px-3 py-2 mt-2 mt-lg-0 ml-lg-3'
                style={{ minWidth: 190 }}
              >
                <span className='text-muted mr-3'>{ scope === PHASE3_PRODUCT_SCOPES.INBOUND_COMPLETED ? 'Item Selesai' : 'Item Pending' }</span>
                <strong className='h4 mb-0'>{ meta.total_count ?? products.length }</strong>
              </div>
            ) }
          </div>
        </CardBody>
      </Card>

      <Card>
        <Phase3ProductQueueTable
          products={ products }
          isLoading={ isLoading }
          scope={ scope }
          onSubmitAppraisal={ handleSubmitAppraisal }
          onConfirmBuyingPrice={ handleConfirmBuyingPrice }
          onRejectBuyingPrice={ handleRejectBuyingPrice }
          onApproveAppraisal={ handleApproveAppraisal }
          onBulkApproveAppraisal={ handleBulkApproveAppraisal }
          onRejectAppraisal={ handleRejectAppraisal }
          onManualGoLive={ handleManualGoLive }
          submittingProductId={ submittingProductId }
          isBulkApproving={ isBulkApproving }
          onAppraisalDirtyChange={ setHasUnsavedAppraisalChanges }
          onClickRow={ handleOpenProduct }
          getProductUrl={ getProductUrl }
          apiNamespace={ apiNamespace }
        />
        { useEnhancedPagination && (
          <Phase3QueuePagination
            currentPage={ Number(meta.current_page) || page }
            totalPages={ Number(meta.total_pages) || 1 }
            totalCount={ Number(meta.total_count) || 0 }
            perPage={ Number(meta.per_page) || perPage }
            perPageOptions={ PAGINATED_QUEUE_PER_PAGE_OPTIONS }
            isLoading={ isLoading }
            onPageChange={ handlePage }
            onPerPageChange={ handlePerPage }
          />
        ) }
      </Card>

      { !useEnhancedPagination && (
        <div className='d-flex justify-content-between align-items-center mt-3'>
          <div className='text-muted'>
            Halaman { meta.current_page || page } dari { meta.total_pages || 1 } — { meta.total_count || products.length } produk
          </div>
          <div>
            <Button
              size='sm'
              className='mr-2'
              disabled={ page <= 1 }
              onClick={ () => handlePage(page - 1) }
            >
              Sebelumnya
            </Button>
            <Button
              size='sm'
              disabled={ meta.total_pages ? page >= meta.total_pages : products.length === 0 }
              onClick={ () => handlePage(page + 1) }
            >
              Berikutnya
            </Button>
          </div>
        </div>
      ) }
    </>
  )
}

Phase3ProductQueuePage.propTypes = propTypes
Phase3ProductQueuePage.defaultProps = defaultProps

export default Phase3ProductQueuePage
